import { useState } from 'react';
import { Icon } from '@iconify/react';
import { Brain, Users, Shield, Mail, Play, Database, Workflow } from 'lucide-react';
import IntegrationLandingBase from '@/components/integrations/IntegrationLandingBase';

export default function GoogleWorkspaceLanding() {
  const [copiedCode, setCopiedCode] = useState(false);

  const handleCopySetupCode = async () => {
    const setupCode = `# 1. Install Memara for Google Workspace
Visit Google Workspace Marketplace

# 2. Connect to your workspace
Authorize Memara with Google Admin

# 3. Start capturing memories
Use Memara in Gmail, Drive, and Meet

# 4. Search workspace knowledge
Access memories across all Google apps`;
    try {
      await navigator.clipboard.writeText(setupCode);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    } catch (error) {
      const textArea = document.createElement('textarea');
      textArea.value = setupCode;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  const handleGetStarted = () => {
    window.location.href = '/login';
  };

  const handleSeeDemo = () => {
    window.open('https://www.youtube.com/watch?v=dQw4w9WgXcQ', '_blank');
  };

  return (
    <IntegrationLandingBase
      registryIntegrationId="google_workspace"
      icon={
        <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-50 shadow" aria-label="Google Workspace Logo">
          <Icon icon="logos:google-workspace" width={48} height={48} />
        </span>
      }
      title="Google Workspace Integration"
      description="Enterprise productivity intelligence across the complete Google ecosystem. Transform Gmail, Drive, Meet, Docs, and Sheets into a unified intelligent knowledge hub with seamless AI-powered memory capture, Google Cloud integration, and organizational productivity optimization."
      features={[
        { icon: <Mail className="h-4 w-4 text-blue-600" />, title: 'Universal Cross-App Memory Intelligence', description: 'Capture and correlate knowledge from Gmail, Drive, Meet, Docs, Sheets, and Calendar with intelligent cross-application context understanding.' },
        { icon: <Brain className="h-4 w-4 text-blue-600" />, title: 'Google AI & Cloud Integration', description: 'Deep integration with Google AI services, Cloud infrastructure, and Vertex AI for enhanced workplace intelligence and organizational insights.' },
        { icon: <Users className="h-4 w-4 text-blue-600" />, title: 'Enterprise Workspace Collaboration', description: 'Shared organizational memory with Google Admin controls, directory synchronization, and enterprise-grade user management across all Google applications.' },
        { icon: <Shield className="h-4 w-4 text-blue-600" />, title: 'Google Cloud Enterprise Security', description: 'Enterprise security with Google Cloud compliance frameworks, data residency controls, and advanced security features integrated with Google\'s security infrastructure.' },
        { icon: <Database className="h-4 w-4 text-blue-600" />, title: 'Unified Productivity Intelligence', description: 'Comprehensive knowledge management across the entire Google Workspace ecosystem with intelligent data correlation and productivity analytics.' },
        { icon: <Workflow className="h-4 w-4 text-blue-600" />, title: 'Google Apps Script & Automation', description: 'Advanced integration with Google Apps Script, workflow automation, and custom business process integration for enterprise productivity optimization.' },
      ]}
      setupSteps={[
        'Install Memara from Google Workspace Marketplace',
        'Connect to your Google Workspace with admin authorization',
        'Configure cross-application memory capture and permissions',
        'Access unified workspace intelligence across all Google applications',
      ]}
      ctas={[
        {
          label: '🚀 Get Started Free',
          onClick: handleGetStarted,
          className: 'bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 h-11 rounded-md text-base font-semibold',
        },
        {
          label: 'See Demo',
          onClick: handleSeeDemo,
          className: 'border-blue-200 text-blue-600 hover:bg-blue-50 px-8 py-4 h-11 rounded-md text-base border',
          icon: <Play className="w-4 h-4 mr-2" />,
        },
        {
          label: copiedCode ? 'Copied!' : 'Copy Setup Code',
          onClick: handleCopySetupCode,
          className: 'border-blue-200 text-blue-600 hover:bg-blue-50 px-8 py-4 h-11 rounded-md text-base border',
          icon: copiedCode ? <Brain className="w-4 h-4 mr-2" /> : <Icon icon="logos:google-workspace" width={20} height={20} />,
        },
      ]}
      videoUrl="https://www.youtube.com/watch?v=dQw4w9WgXcQ"
      microcopy="Trusted by enterprise organizations, Google Workspace administrators, and productivity teams using Google's complete ecosystem for business collaboration and organizational knowledge management."
      trustedBy="Google, Enterprise Productivity Teams, and more."
      testimonial="Memara's Google Workspace integration creates an unprecedented unified intelligence layer across our entire Google ecosystem, transforming every email, document, meeting, and collaboration into searchable organizational knowledge with enterprise-grade security and Google Cloud integration."
    />
  );
}